import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Role, UserRoleVM } from 'app/_models';
import { DialogsService } from 'app/_services/dialogs/dialogs.service';
import { NotificationService } from 'app/_services/notification.service';
import { UserRolesService } from 'app/_services/users-roles.service';
import { EditUserDialogComponent } from '../edit-user-dialog/edit-user-dialog.component';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['../../shared/table-defaults.scss', './users.component.scss'],
})
export class UsersComponent implements OnInit {
    value = '';
    sapidList: string[] = [];
    spUsers: any[];
    dataSource: MatTableDataSource<UserRoleVM>;
    selection = new SelectionModel<UserRoleVM>(true, []);
    removeItems = false;

    displayedColumns: string[] = ['firstName', 'lastName', 'email', 'roleName', 'actions'];
    columnsToDisplay = this.displayedColumns.slice();

    users: UserRoleVM[];
    roles: Role[];

    @ViewChild(MatTable) table: MatTable<UserRoleVM[]>;
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        public dialog: MatDialog,
        private dialogService: DialogsService,
        private userRolesSerivce: UserRolesService,
        private notificationService: NotificationService
    ) {}

    ngOnInit(): void {
        this.userRolesSerivce.usersDataSource.subscribe((users) => {
            this.loadDatasource(users);
            this.users = users;
        });

        this.userRolesSerivce.rolesDataSource.subscribe((roles) => {
            this.roles = roles;
        });
    }

    loadDatasource(data): void {
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        // Anytime the datasource is updated, reset our list of SAPIDs
        this.getSapidList(data);
    }

    getSapidList(data?: any) {
        this.sapidList = data.map((user) => user.sapid);
    }

    applyFilter(searchTerms: any): void {
        this.dataSource.filter = searchTerms;

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    clearFilter(): void {
        this.dataSource.filter = '';

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle(): void {
        this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach((row) => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: UserRoleVM): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.userRoleId + 1}`;
    }

    toggleSelectUsers(): void {
        const columns = this.displayedColumns;
        if (!columns.includes('select')) {
            this.displayedColumns = ['select', ...columns];
            this.removeItems = true;
        }
    }

    cancelEdit(): void {
        this.displayedColumns = this.columnsToDisplay;
        this.removeItems = false;
        this.selection.clear();
    }

    openEditUserDialog(userRole: any, title: string, creating = false): MatDialogRef<EditUserDialogComponent> {
        const roles = this.roles;
        const sapidList = this.sapidList;
        const config: MatDialogConfig = {
            // disableClose: true,
            width: '450px',
            panelClass: 'edit-userrole',
            data: { creating, userRole, roles, sapidList, title },
        };

        return this.dialog.open(EditUserDialogComponent, config);
    }

    addUser(): void {
        const users = this.users;
        const creating = true;
        const title = 'Add New User';
        const newUser = {
            userRoleId: 0, // Set to 0 since column is set to Identity in database
            sapid: '',
            roleId: 1,
        };

        const dialogResult = this.openEditUserDialog(newUser, title, creating);

        dialogResult.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.userRolesSerivce.createUser(result).subscribe((addedUser: UserRoleVM) => {
                this.userRolesSerivce.getUserById(addedUser.userRoleId).subscribe((res: UserRoleVM) => {
                    users.push(res);
                    this.loadDatasource(this.users);
                    this.userRolesSerivce.getRoles();
                    this.notificationService.showSuccess(`New User has been added.`);
                });
            });
        });
    }

    editUser(user: UserRoleVM) {
        const title = 'Edit User Role';

        const dialogResult = this.openEditUserDialog(user, title);

        dialogResult.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            //Prepare updated object for the database
            const userRole = {
                userRoleId: result.userRoleId,
                roleId: result.roleId,
                sapid: result.sapid,
            };

            this.userRolesSerivce.updateUser(userRole.userRoleId, userRole).subscribe((_) => {
                this.userRolesSerivce.getRoles();

                this.getSapidList(this.users);
                this.table.renderRows();
                this.notificationService.showSuccess(`User's role has been updated.`);
            });
        });
    }

    deleteUsers(selection: SelectionModel<UserRoleVM>): void {
        const users = this.dataSource.data.filter((user) => selection.selected.includes(user));

        const dialogResult$ = this.dialogService.OpenConfirmationDialog({
            TitleText: 'Delete Users',
            MessageText: 'Are you sure you want to delete these users?',
            OKText: 'Yes, remove user(s)',
            CancelText: 'No',
        });

        dialogResult$.subscribe((result) => {
            if (!result) {
                return;
            }

            this.userRolesSerivce.deleteMultipleUsers(users).subscribe((res) => {
                //TODO: Check this with other CRUD functions for differences between datasource and list data
                this.users = this.dataSource.data.filter((user) => !selection.selected.includes(user));

                this.loadDatasource(this.users);
                this.userRolesSerivce.getRoles();
                this.notificationService.showSuccess(`${selection.selected.length} Users have been remvoed.`);

                // Reset table columns to remove selection columns
                this.displayedColumns = this.columnsToDisplay;
                this.removeItems = false;
                this.selection.clear();
            });
        });
    }
}
